import { Inject } from '@odinjs/odin';

import { Endpoint, FormViewService, OptionKey, ProviderAccreditationPendenciesService, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateMenu, HPMSOnOptionClick } from '@philips-emr/hpms-provider-accreditation-components';
import { FetchAPI, FormAPI, PanelAPI, ToastNotificationAPI } from '@philips-emr/tws-components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnCancel, OnClick, OnClose, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { ProviderAccreditationMedicineMaterialsAliases as Aliases } from '../../codegen';
import BaseController from '../BaseController';

import { AccreditationSteps } from './provider-accreditation-controller';

type MyException = {
  body: string,
};

@Controller({ fragment: 'provider-accreditation-medicine-materials' })
export default class ProviderAccreditationOutsourcedServices extends BaseController {

  @Inject('fetch-medicine-materials-list')
  private fetchMedicineMaterialsList: FetchAPI;

  @Inject('form-medicine-material')
  private formMedicineMaterial: FormAPI;

  @Inject('panel-pendencies')
  private panelPendencies: PanelAPI;

  @Inject('toast-notification')
  private toast: ToastNotificationAPI;

  @Inject(Aliases.PANEL_MODAL)
  private panelModal: PanelAPI;

  @HPMSOnOptionClick('hpms-custom-card-medicine-materials')
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {

    const { nrSeqAccreditation, nrSeqUser } = this.historyService.getCurrentRouteParams();
    const { data, key } = event;
    const { nrSequence } = data;

    const action = {
      [OptionKey.DELETE]: () => this.deleteRecord(Number(nrSeqUser), Number(nrSeqAccreditation), Number(nrSequence)),
      [OptionKey.EDIT]: () => this.editRecord(Number(nrSequence)),
    };

    if (action[key]) {
      action[key]();
    }
  }

  @OnMount('panel-form')
  onMountPanelForm(event: MountEvent): void {
    const api = event.target as PanelAPI;
    FormViewService.shouldRenderForm(api, AccreditationSteps.MEDICINE_MATERIALS);
  }

  @HPMSOnCreateMenu('hpms-custom-card-medicine-materials')
  onCreateMenu(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, shouldRender } = event;
    const { editable } = data;
    if (editable == 'N') {
      shouldRender(false);
    }
  }

  private editRecord(nrSequence: number): void {
    this.panelModal.setVisible(true);
    this.context.set({ nrSeqMaterial : nrSequence });
  }

  private deleteRecord(nrSeqUser: number, nrSeqAccreditation: number, nrSequence: number): void {
    void HttpService.delete(Endpoint.DELETE.MEDICINE_MATERIAL, { nrSeqUser, nrSequence, nrSeqAccreditation })
      .then(() => {
        this.fetchMedicineMaterialsList.forceFetch();
      }).catch(() => {
        ToastServiceProviderAccreditation.error('pa.error-process-request');
        this.fetchMedicineMaterialsList.forceFetch();
      });
  }

  @OnSubmit('form-medicine-material')
  onSubmitForm(event: FormAPI): void {
    const body = event.record;
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    this.formMedicineMaterial.setLoading(true);
    void HttpService.post(Endpoint.POST.MATERIALS, { nrSeqUser, nrSeqAccreditation }, { body })
      .then(() => {
        this.formMedicineMaterial.setLoading(false);
        this.fetchMedicineMaterialsList.forceFetch();
        this.panelModal.setVisible(false);
      }).catch(({ body: respBody }: MyException) => {
        this.formMedicineMaterial.setLoading(false);

        if (respBody !== '') {
          ToastServiceProviderAccreditation.error(respBody);
        } else {
          ToastServiceProviderAccreditation.error('pa.error-process-request');
        }
      });
  }

  @OnCancel(Aliases.FORM_MEDICINE_MATERIAL)
  onCancelFormAdd() : void {
    this.panelModal.setVisible(false);

  }

  @OnMount('fetch-pendencies')
  onMountFetchPendencies(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.MEDICINE_MATERIALS);
  }

  @OnAfterFetch('fetch-pendencies')
  onAfterFetch(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelPendencies.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toast, event);
    }
  }

  @OnClick(Aliases.ACTION_ADD)
  onClickAction() : void {
    this.panelModal.setVisible(true);
  }

  @OnClose(Aliases.MODAL_MATERIAL)
  onCloseModal() : void {
    this.panelModal.setVisible(false);
  }

  @OnMount(Aliases.FETCH_MEDICINE_MATERIAL)
  onMountModal(event : MountEvent) : void {
    const api = event.target as FetchAPI;
    const { nrSeqMaterial } = this.context.getBulkData();
    if (nrSeqMaterial != undefined) {
      api.setParameters({ nrSequence: nrSeqMaterial });
      api.forceFetch();
    }
  }

  @OnAfterFetch(Aliases.FETCH_MEDICINE_MATERIAL)
  onAfterFetchMaterial()  : void {
    this.context.set({ nrSeqMaterial : null });
  }
}
